import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import { buildReferralLink, loginRedirectURL, servicesSubbrokerLogoutURL } from '../utils/Urls'
import { THEMES } from '../utils/theme'
import { useThemeCtx } from './ThemeProvider'
import { fetchResolvedTickets, loadSeenResolved, saveSeenResolved } from '../utils/tickets'
import { clearSessionDataCache, getProfileRequest, getReferralRequest } from '../utils/sessionData'

const TicketModal = lazy(() => import('./TicketModal'))

// --- inline nav icons (match the design system) ---
const IconDash = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9.5 12 3l9 6.5" /><path d="M5 10v10h14V10" /></svg>
const IconKyc = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M8 9h4M8 13h8M8 17h6" /></svg>
const IconLedger = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 5h16M4 12h16M4 19h10" /></svg>
const IconReports = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></svg>
const IconPledge = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="8" width="18" height="12" rx="2" /><path d="M7 8V6a5 5 0 0 1 10 0v2" /></svg>
const IconContacts = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3.2" /><path d="M3 20a6 6 0 0 1 12 0M16 11h5M18.5 8.5v5" /></svg>
const IconPayout = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2.5" y="6" width="19" height="12" rx="2" /><path d="M2.5 10h19M6 15h4" /></svg>
const IconProfile = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="3.6" /><path d="M5 20a7 7 0 0 1 14 0" /></svg>
const IconLogout = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l-5-5 5-5M4 12h11" /></svg>
const IconTicket = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 9V7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4Z" /><path d="M13 5v14" /></svg>
const IconProposals = () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 3h8l4 4v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" /><path d="M15 3v4h4M8.5 12h7M8.5 15.5h7M8.5 8.5h3" /></svg>

const NAV = [
  { label: 'Home', icon: <IconDash />, to: '/' },
  { label: 'KYC & Onboarding', icon: <IconKyc />, external: true },
  { label: 'Ledgers', icon: <IconLedger />, to: '/account' },
  { label: 'Reports', icon: <IconReports />, to: '/reports' },
  { label: 'Pledge / Unpledge', icon: <IconPledge />, to: '/pledgeorunpledge' },
  { label: 'Contacts', icon: <IconContacts />, to: '/contacts' },
  { label: 'Proposals', icon: <IconProposals />, to: '/proposals' },
  { label: 'Payout', icon: <IconPayout />, to: '/payout' },
  { label: 'Profile', icon: <IconProfile />, to: '/profile' },
]

export default function Shell({ children }) {
  const history = useHistory()
  const { pathname } = useLocation()
  const { theme, changeTheme } = useThemeCtx()
  const [name, setName] = useState(() => localStorage.getItem('firstName') || '')
  const [referralFragment, setReferralFragment] = useState(null)
  const [ticketOpen, setTicketOpen] = useState(false)
  const [ticketTab, setTicketTab] = useState('new')
  // #44188: below 760px the sidebar is an off-canvas drawer opened from the
  // topbar menu button (it used to be display:none, leaving phones no navigation).
  const [navOpen, setNavOpen] = useState(false)

  const sideNavRef = useRef(null)
  const toggleBtnRef = useRef(null)

  // #48351: both helpers are used inside the drawer effects below, so they are
  // memoised with empty deps (refs and setNavOpen are stable) and listed in those
  // dependency arrays. Plain functions would be re-created every render and left
  // out of the deps -- the stale-closure pattern React's lint rule warns about.
  const closeNav = useCallback((restoreFocus = true) => {
    setNavOpen(false)
    if (restoreFocus && toggleBtnRef.current) {
      toggleBtnRef.current.focus()
    }
  }, [])

  const getFocusableElements = useCallback(() => {
    if (!sideNavRef.current) return []
    return Array.from(
      sideNavRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => {
      const style = window.getComputedStyle ? window.getComputedStyle(el) : el.style
      return style?.display !== 'none' && style?.visibility !== 'hidden'
    })
  }, [])

  useEffect(() => {
    setNavOpen(false)
  }, [pathname])

  // Move focus into drawer on open.
  // #48351: this used to wait a fixed 30 ms, a guess against the .25s drawer
  // transition (theme.css:290) that a slow device could outrun. No wait is needed.
  // The only thing that could block focus is the closed drawer's visibility:hidden,
  // and visibility flips to visible at the START of the transition, not the end --
  // by the time this passive effect runs React has committed .nav-open and the
  // browser has painted it. getFocusableElements() reads getComputedStyle, which
  // forces any pending style recalculation, and a still-sliding transform does not
  // affect focusability. So focus synchronously and drop the magic number.
  useEffect(() => {
    if (!navOpen) return
    const focusables = getFocusableElements()
    focusables[0]?.focus()
  }, [navOpen, getFocusableElements])

  // Accessibility: Escape key closes the drawer; Tab is trapped inside
  useEffect(() => {
    if (!navOpen) return

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        closeNav()
        return
      }

      if (e.key === 'Tab') {
        const focusables = getFocusableElements()
        if (focusables.length === 0) {
          e.preventDefault()
          return
        }

        const firstEl = focusables[0]
        const lastEl = focusables[focusables.length - 1]

        if (e.shiftKey) {
          if (document.activeElement === firstEl || !sideNavRef.current?.contains(document.activeElement)) {
            e.preventDefault()
            lastEl.focus()
          }
        } else {
          if (document.activeElement === lastEl || !sideNavRef.current?.contains(document.activeElement)) {
            e.preventDefault()
            firstEl.focus()
          }
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [navOpen, closeNav, getFocusableElements])

  // Resolved-ticket notifications (bell). Poll the tickets the user raised from
  // here and surface any that the CRM has marked Resolved/Closed.
  const [notifOpen, setNotifOpen] = useState(false)
  const [resolved, setResolved] = useState([])
  const [unread, setUnread] = useState(0)
  // Ids already known-resolved. null until the first poll so the initial load
  // sets a baseline WITHOUT toasting old resolutions (the unread dot covers those).
  const knownResolvedRef = useRef(null)

  useEffect(() => {
    getProfileRequest()
      .then((d) => { if (d.data.status === 'success') { setName(d?.data?.SubbrokerID ?? ''); localStorage.setItem('firstName', d?.data?.SubbrokerID ?? '') } })
      .catch(() => { })
    getReferralRequest()
      .then((d) => { if (d.data.status === 'success') setReferralFragment(d?.data?.Referral_Link) })
      .catch(() => { })
  }, [])

  useEffect(() => {
    let cancelled = false
    const refresh = async () => {
      const raw = await fetchResolvedTickets()
      const list = Array.isArray(raw) ? raw : []
      if (cancelled) return
      setResolved(list)
      const seen = new Set(loadSeenResolved())
      setUnread(list.filter((t) => !seen.has(t.id)).length)
      if (knownResolvedRef.current === null) {
        // First poll: baseline only, no toasts for already-resolved tickets.
        knownResolvedRef.current = new Set(list.map((t) => t.id))
      } else {
        // Later polls: toast anything that just transitioned to resolved.
        list.forEach((t) => {
          if (!knownResolvedRef.current.has(t.id)) {
            knownResolvedRef.current.add(t.id)
            toast.success(`Ticket #${t.id} resolved`, { icon: '✅' })
          }
        })
      }
    }
    refresh()
    const iv = setInterval(refresh, 120000)
    return () => { cancelled = true; clearInterval(iv) }
  }, [])

  const openTicket = (tab) => { setTicketTab(tab || 'new'); setTicketOpen(true); closeNav() }

  // #43947: Neuron's subbroker KYC page is gone and the CRM login is staff-only,
  // so KYC & Onboarding opens account opening on kyc.adityatrading.in with this
  // subbroker's referral code - the same link the Dashboard shares. Never open it
  // without the code: an account opened that way is not mapped to the subbroker.
  const openKyc = () => {
    const code = (localStorage.getItem('subbroker_code') || name || '').trim()
    const fragment = referralFragment || (code ? `sb=${encodeURIComponent(code)}` : '')
    if (!fragment) {
      toast.error('Your subbroker code is still loading. Please try again in a moment.')
      return
    }
    window.open(buildReferralLink(fragment), '_blank', 'noopener,noreferrer')
    closeNav()
  }

  const toggleNotif = () => {
    if (!notifOpen) {
      // Acknowledge everything currently resolved -> clears the unread dot.
      saveSeenResolved([...loadSeenResolved(), ...resolved.map((t) => t.id)])
      setUnread(0)
    }
    setNotifOpen((o) => !o)
  }

  const logout = async () => {
    try {
      await axios.post(servicesSubbrokerLogoutURL, {}, { withCredentials: true })
    } catch (e) { /* redirect even if the session already expired */ }
    localStorage.removeItem('subbroker_code')
    localStorage.removeItem('mobileNumber')
    localStorage.removeItem('firstName')
    clearSessionDataCache()
    window.location.href = loginRedirectURL
  }

  const isActive = (item) => !item.external && (item.to === '/' ? pathname === '/' : pathname.startsWith(item.to))
  const avatar = (name || 'AT').slice(0, 2).toUpperCase()

  const logoSrc = `${process.env.PUBLIC_URL || ''}/ats-logo.png`

  return (
    <div className={`ats-app${navOpen ? ' nav-open' : ''}`}>
      {/* SIDEBAR (drawer on mobile) */}
      <aside
        className="side"
        id="ats-side-nav"
        ref={sideNavRef}
        role={navOpen ? 'dialog' : undefined}
        aria-modal={navOpen ? 'true' : undefined}
        aria-label={navOpen ? 'Navigation menu' : undefined}
      >
        <div className="brand" onClick={() => { history.push('/'); closeNav(false) }} style={{ cursor: 'pointer' }}>
          <img src={logoSrc} alt="Aditya Trading Solutions" style={{ height: 34, width: 'auto' }} />
        </div>
        <div className="nav-label">Menu</div>
        <nav className="nav">
          {NAV.map((item) => item.external ? (
            <button key={item.label} type="button" onClick={openKyc}>
              {item.icon}{item.label}
            </button>
          ) : (
            <Link key={item.label} className={isActive(item) ? 'active' : ''} to={item.to} onClick={() => closeNav(false)}>
              {item.icon}{item.label}
            </Link>
          ))}
          <a href="#" onClick={(e) => { e.preventDefault(); openTicket('new') }}><IconTicket />Raise a Ticket</a>
        </nav>
        <div className="side-foot">
          {/* The topbar has no room for the theme switcher on phones, so it moves here. */}
          <div className="themes side-themes">
            {THEMES.map((t) => (
              <button key={t.id} className={theme === t.id ? 'on' : ''} onClick={() => changeTheme(t.id)}>{t.label}</button>
            ))}
          </div>
          <nav className="nav">
            <button onClick={logout}><IconLogout />Log out</button>
          </nav>
        </div>
      </aside>
      {navOpen && <div className="nav-backdrop" onClick={() => closeNav(true)} aria-hidden="true" />}

      {/* MAIN */}
      <div className="main">
        <header className="topbar">
          <button
            ref={toggleBtnRef}
            className="icon-btn nav-toggle"
            onClick={() => setNavOpen((o) => !o)}
            aria-label={navOpen ? 'Close menu' : 'Open menu'}
            aria-controls="ats-side-nav"
            aria-expanded={navOpen}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <img className="top-logo" src={logoSrc} alt="Aditya Trading Solutions" onClick={() => history.push('/')} />
          <div className="top-right">
            <button onClick={() => openTicket('new')} title="Create a support ticket"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid var(--accent)', background: 'var(--accent)', color: '#fff', borderRadius: 10, padding: '8px 13px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-ui)', whiteSpace: 'nowrap' }}>
              + New Ticket
            </button>
            <div className="themes">
              {THEMES.map((t) => (
                <button key={t.id} className={theme === t.id ? 'on' : ''} onClick={() => changeTheme(t.id)}>{t.label}</button>
              ))}
            </div>

            {/* Notifications — resolved tickets */}
            <div style={{ position: 'relative' }}>
              <button className="icon-btn" onClick={toggleNotif} title="Notifications">
                {unread > 0 && <span className="dot"></span>}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0" /></svg>
              </button>
              {notifOpen && (
                <>
                  <div onClick={() => setNotifOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                  <div className="notif-pop" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, boxShadow: 'var(--shadow)', zIndex: 50, overflow: 'hidden', fontFamily: 'var(--font-ui)' }}>
                    <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--ink)' }}>Notifications</span>
                      <span style={{ fontSize: 11, color: 'var(--muted)' }}>{resolved.length} resolved</span>
                    </div>
                    <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                      {resolved.length === 0 ? (
                        <div style={{ padding: '28px 16px', textAlign: 'center', color: 'var(--muted)', fontSize: 12.5 }}>
                          No resolved tickets yet.<br />You'll be notified here when a ticket you raised is resolved.
                        </div>
                      ) : resolved.map((t) => (
                        <button key={t.id} onClick={() => { setNotifOpen(false); openTicket('mine') }}
                          style={{ display: 'block', width: '100%', textAlign: 'left', border: 0, borderBottom: '1px solid var(--border)', background: 'transparent', padding: '11px 14px', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--pos)', background: 'var(--pos-soft)', borderRadius: 99, padding: '2px 8px', flex: 'none' }}>Resolved</span>
                            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.subject}</span>
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>#{t.id}{t.category ? ' · ' + t.category : ''}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="avatar">{avatar}</div>
          </div>
        </header>

        <div className="content">
          <div className="content-inner">
            {children}
          </div>
        </div>
      </div>
      {ticketOpen && <Suspense fallback={null}><TicketModal open={ticketOpen} onClose={() => setTicketOpen(false)} initialTab={ticketTab} /></Suspense>}
    </div>
  )
}
